import React, { useState, useEffect } from "react";
import { isSuperAdmin } from "../../authGuard";
import { toastService } from "../../services/toastService";
import { useNilWhitelistStore } from "../../stores/NilWhitelistStore";
import { LoadingState, ErrorState, EmptyState } from "./components/SanctionsListStates";
import { SanctionsListContent } from "./components/SanctionsListContent";
import { NilWhitelistProps, NilWhitelistLocalState } from "./components/types";
import { utils } from "../../utils/utils";
import { WhitelistModal } from "./components/WhiteModal/WhitelistModal";
import { JsonModal } from "./components/JsonModal/JsonModal";
import { Info, Filter } from "lucide-react";

// Initial state factory
const createInitialState = (): NilWhitelistLocalState => ({
    showJsonInfoModal: false,
    showFunnelModal: false,
    showWhitelistModal: false,
    selectedListData: null,
    combinedListData: null,
    reason: "",
    attachment: null,
    reasonError: "",
    attachmentError: "",
});

const useSanctionsData = (cbsId: string | undefined) => {
    const store = useNilWhitelistStore();

    const loadSanctions = async () => {
        if (!cbsId) return;
        
        await store.loadSanctions(cbsId);
        
        if (store.loadError) {
            toastService.error(store.loadError);
        }
    };

    useEffect(() => {
        loadSanctions();
    }, [cbsId]);

    return { store, loadSanctions };
};


// Main Component
const NilWhitelistComponent: React.FC<NilWhitelistProps> = ({ cbsId }) => {
    const { store } = useSanctionsData(cbsId);
    const [state, setState] = useState<NilWhitelistLocalState>(createInitialState());

    // State update helpers
    const updateState = (updates: Partial<NilWhitelistLocalState>) => {
        setState(prev => ({ ...prev, ...updates }));
    };

    const resetWhitelistForm = () => {
        updateState({
            showWhitelistModal: false,
            reason: "",
            attachment: null,
            reasonError: "",
            attachmentError: "",
        });
    };

    // Event handlers
    const handleCheckboxChange = (listId: string, checked: boolean) => {
        store.toggleCheckbox(listId, checked);
    };

    const handleUncheckAll = () => {
        // Uncheck all checkboxes efficiently
        const checkedIds = Array.from(store.checkedLists);
        checkedIds.forEach(listId => {
            store.toggleCheckbox(listId, false);
        });
    };

    const handleInfoClick = (listData: any) => {
        updateState({
            selectedListData: listData,
            showJsonInfoModal: true,
        });
    };

    const handleFunnelClick = () => {
        updateState({
            combinedListData: store.detailedJson,
            showFunnelModal: true,
        });
    };

    const handleSubmitClick = () => {
        const allUnchecked = store.sanctionsLists.length > 0 && store.checkedLists.size === 0;
        
        if (!allUnchecked) {
            toastService.error("Please uncheck all sanctions lists to proceed.");
            return;
        }
        
        updateState({ showWhitelistModal: true });
    };

    const handleWhitelistConfirm = async () => {
        const validation = utils.validateWhitelistForm(state.reason, state.attachment);
        
        updateState({
            reasonError: validation.reasonError,
            attachmentError: validation.attachmentError,
        });
        
        if (!validation.isValid || !cbsId) {
            return;
        }

        const uncheckedIds = store.sanctionsLists
            .filter(list => !store.checkedLists.has(list.id))
            .map(list => list.id);

        const success = await store.submitWhitelist({
            cbsId,
            watchlistEntryIds: uncheckedIds,
            reason: state.reason,
            attachment: state.attachment!,
        });

        if (success) {
            resetWhitelistForm();
            toastService.success("Customer whitelisted successfully.");
            
            // Reload sanctions list after successful whitelist
            await store.loadSanctions(cbsId);
        } else {
            toastService.error(store.submitError || "Whitelist action failed. Please try again.");
        }
    };

    const handleWhitelistCancel = () => {
        resetWhitelistForm();
    };

    const handleFileSelect = (event: any) => {
        const file = utils.extractFileFromEvent(event);
        
        if (!file) {
            updateState({ attachment: null, attachmentError: "" });
            return;
        }

        const validation = utils.validateWhitelistFile(file);
        
        updateState({
            attachment: validation.isValid ? file : null,
            attachmentError: validation.error,
        });
    };

    const handleReasonChange = (value: string) => {
        updateState({ reason: value, reasonError: "" });
    };

    const toggleJsonInfoModal = () => {
        updateState({ showJsonInfoModal: !state.showJsonInfoModal });
    };

    const toggleFunnelModal = () => {
        updateState({ showFunnelModal: !state.showFunnelModal });
    };

    const handleJsonCopy = () => {
        toastService.success("JSON copied to clipboard");
    };

    // Render helpers
    const renderSanctionsLists = () => {
        if (store.isLoading) {
            return <LoadingState />;
        }

        if (store.loadError) {
            return <ErrorState error={store.loadError} />;
        }

        if (store.sanctionsLists.length === 0) {
            return <EmptyState />;
        }

        return (
            <SanctionsListContent
                sanctionsLists={store.sanctionsLists}
                checkedLists={store.checkedLists}
                onCheckboxChange={handleCheckboxChange}
                onInfoClick={handleInfoClick}
                onFunnelClick={handleFunnelClick}
                onSubmitClick={handleSubmitClick}
                onUncheckAll={handleUncheckAll}
            />
        );
    };

    // Auto-clear attachment error when valid file is selected
    useEffect(() => {
        if (state.attachment && state.attachmentError) {
            updateState({ attachmentError: "" });
        }
    }, [state.attachment]);

    // Clear errors when modal opens with existing file
    useEffect(() => {
        if (state.showWhitelistModal && state.attachment && state.attachmentError) {
            updateState({ attachmentError: "" });
        }
    }, [state.showWhitelistModal]);

    if (!isSuperAdmin() || !cbsId) {
        return null;
    }

    return (
        <>
            <div>
                {renderSanctionsLists()}
            </div>

            <WhitelistModal
                isOpen={state.showWhitelistModal}
                onHide={handleWhitelistCancel}
                reason={state.reason}
                attachment={state.attachment}
                reasonError={state.reasonError}
                attachmentError={state.attachmentError}
                isSubmitting={store.isSubmitting}
                onReasonChange={handleReasonChange}
                onFileSelect={handleFileSelect}
                onConfirm={handleWhitelistConfirm}
                onCancel={handleWhitelistCancel}
            />

            <JsonModal
                isOpen={state.showJsonInfoModal}
                toggle={toggleJsonInfoModal}
                title="Sanctions List Details"
                icon={<Info size={24} />}
                data={state.selectedListData}
                onCopy={handleJsonCopy}
            />

            <JsonModal
                isOpen={state.showFunnelModal}
                toggle={toggleFunnelModal}
                title="Combined Sanctions Data"
                icon={<Filter size={24} />}
                data={state.combinedListData}
                onCopy={handleJsonCopy}
            />
        </>
    );
};

export default NilWhitelistComponent;
